import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatError } from "@/lib/api";
import ProfileCard from "@/components/umaang/ProfileCard";
import Combobox from "@/components/umaang/Combobox";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Filter, Search, Flame, ShieldCheck, Crown, Heart, MapPin, X } from "lucide-react";
import { Link } from "react-router-dom";
import { RELIGIONS, DIETS, DIET_VALUES, MARITAL_STATUS, CITIES, STATES, COUNTRIES } from "@/lib/refdata";
import { getBrowserPosition } from "@/lib/geo";

const TABS = [
  { key: "", label: "All", icon: Search, color: "#1C1917" },
  { key: "new", label: "New", icon: Flame, color: "#F59E0B" },
  { key: "verified", label: "Verified", icon: ShieldCheck, color: "#4F7942" },
  { key: "premium", label: "Premium", icon: Crown, color: "#B45309" },
  { key: "mymatch", label: "My Match", icon: Heart, color: "#C85A53" },
  { key: "nearme", label: "Near Me", icon: MapPin, color: "#2563EB" },
];

const emptyFilters = {
  q: "", city: "", state: "", country: "",
  diet: "", marital_status: "", religion: "", has_children: "",
};

export default function Home() {
  const { user } = useAuth();
  const { t } = useLang();
  const [profiles, setProfiles] = useState([]);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [geoCoords, setGeoCoords] = useState(null); // { latitude, longitude }
  const [usedGeo, setUsedGeo] = useState(false);
  const sentRef = useRef(null);

  // When Near Me tab is activated, try to grab a fresh browser position.
  // Silent fallback: backend uses stored user loc or city.
  useEffect(() => {
    if (tab !== "nearme") return;
    let cancel = false;
    (async () => {
      const p = await getBrowserPosition();
      if (!cancel && p) setGeoCoords({ latitude: p.latitude, longitude: p.longitude });
    })();
    return () => { cancel = true; };
  }, [tab]);

  const load = useCallback(async (reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const params = { skip: reset ? 0 : skip, limit: 12 };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) params[k] = v; });
      if (tab) params.tab = tab;
      if (tab === "nearme" && geoCoords) {
        params.lat = geoCoords.latitude;
        params.lng = geoCoords.longitude;
        params.radius_km = 100;
      }
      if (params.has_children === "yes") params.has_children = true;
      else if (params.has_children === "no") params.has_children = false;
      const { data } = await api.get("/profiles", { params });
      setProfiles((prev) => (reset ? data.profiles : [...prev, ...data.profiles]));
      setTotal(data.total);
      setUsedGeo(!!data.used_geo);
      setSkip((reset ? 0 : skip) + data.profiles.length);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }, [skip, filters, tab, loading]);

  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    const el = sentRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && profiles.length < total && !loading) load(false);
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [load, profiles.length, total, loading]);

  const applyFilters = () => { setSkip(0); load(true); };
  const clearFilters = () => { setFilters(emptyFilters); setSkip(0); setTimeout(() => load(true), 0); };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  // Quiz nudge — check if user completed AI quiz (dismissible per session)
  const [quizStatus, setQuizStatus] = useState(null);
  const [quizNudgeDismissed, setQuizNudgeDismissed] = useState(() => sessionStorage.getItem("umg_quiz_nudge") === "1");
  useEffect(() => {
    api.get("/quiz/status").then(({ data }) => setQuizStatus(data)).catch(() => {});
  }, []);
  const showQuizNudge = quizStatus && !quizStatus.completed && !quizNudgeDismissed;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-[#C85A53] font-semibold">Discover</div>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Rishtay chosen for you</h1>
          <p className="text-[color:var(--umg-text-2)] mt-2">{total} verified matches · sorted by compatibility</p>
        </div>
        {!user?.is_premium && (
          <Link to="/premium" data-testid="upgrade-cta">
            <Button className="btn-pill bg-[#1C1917] text-white hover:bg-[#111]">
              <Sparkles className="w-4 h-4 mr-1.5" /> {t("upgrade")}
            </Button>
          </Link>
        )}
      </div>

      {/* Quiz nudge — one-tap CTA to unlock AI-matched suggestions */}
      {showQuizNudge && (
        <div data-testid="quiz-nudge" className="mt-6 relative overflow-hidden rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6" style={{ borderColor: "#DDD6FE", background: "linear-gradient(90deg,#F5F3FF 0%,#FCE7F3 60%,#FEF3C7 100%)" }}>
          <div className="w-11 h-11 rounded-xl grid place-items-center bg-white shadow-sm text-[#7C3AED] shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg text-[#5B21B6]">Unlock AI-matched suggestions ✨</div>
            <p className="text-xs text-[#6D28D9] mt-1">
              Sirf 60 seconds · 8 quick questions · aapko personality, lifestyle aur values-based real compatibility score milega — <b>{quizStatus?.answered || 0}/{quizStatus?.total || 8}</b> answered so far.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/quiz" data-testid="quiz-nudge-cta" className="btn-pill bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white text-sm px-4 py-2 rounded-full whitespace-nowrap">
              {(quizStatus?.answered || 0) > 0 ? "Resume →" : "Take quiz →"}
            </Link>
            <button
              data-testid="quiz-nudge-dismiss"
              onClick={() => { sessionStorage.setItem("umg_quiz_nudge", "1"); setQuizNudgeDismissed(true); }}
              className="text-[color:var(--umg-text-2)] hover:text-red-500 p-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-2 flex-wrap">
        {TABS.map((T) => {
          const active = tab === T.key;
          return (
            <button
              key={T.key || "all"}
              data-testid={`home-tab-${T.key || "all"}`}
              onClick={() => setTab(T.key)}
              style={active ? { background: T.color, borderColor: T.color, color: "white", boxShadow: `0 4px 14px ${T.color}55` } : {}}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition ${
                active ? "" : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
              }`}
            >
              <T.icon className="w-3.5 h-3.5" style={!active ? { color: T.color } : {}} /> {T.label}
            </button>
          );
        })}
      </div>

      {/* Near Me status banner */}
      {tab === "nearme" && (
        <div data-testid="nearme-banner" className="mt-4 text-xs px-3 py-2 rounded-full inline-flex items-center gap-1.5 border" style={{ borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
          <MapPin className="w-3 h-3 text-[#C85A53]" />
          {usedGeo
            ? "Showing matches within 100 km of you"
            : "Geo unavailable — showing profiles from your city. Enable location in your profile for a precise 100 km radius."}
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 p-4 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--umg-text-2)]">
            <Filter className="w-3.5 h-3.5" /> Filters {activeFilterCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#C85A53] text-white rounded-full text-[10px]">{activeFilterCount}</span>}
          </div>
          {activeFilterCount > 0 && (
            <button data-testid="clear-filters" onClick={clearFilters} className="text-xs text-[#C85A53] hover:underline flex items-center gap-1">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          <Input
            data-testid="filter-search"
            placeholder="Search by name"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="border-[color:var(--umg-border)]"
          />
          <Combobox testId="filter-city" value={filters.city} onChange={(v) => setFilters({ ...filters, city: v })} options={[{ value: "", label: "Any city" }, ...CITIES]} placeholder="City" />
          <Combobox testId="filter-state" value={filters.state} onChange={(v) => setFilters({ ...filters, state: v })} options={[{ value: "", label: "Any state" }, ...STATES]} placeholder="State" />
          <Combobox testId="filter-country" value={filters.country} onChange={(v) => setFilters({ ...filters, country: v })} options={[{ value: "", label: "Any country" }, ...COUNTRIES]} placeholder="Country" />
          <Combobox testId="filter-religion" value={filters.religion} onChange={(v) => setFilters({ ...filters, religion: v })} options={[{ value: "", label: "Any religion" }, ...RELIGIONS]} placeholder="Religion" />
          <Combobox testId="filter-marital" value={filters.marital_status} onChange={(v) => setFilters({ ...filters, marital_status: v })} options={[{ value: "", label: "Any marital status" }, ...MARITAL_STATUS]} placeholder="Marital status" />
          <Combobox testId="filter-diet" value={filters.diet} onChange={(v) => setFilters({ ...filters, diet: v })} options={[{ value: "", label: "Any diet" }, ...DIETS.map((d) => ({ value: DIET_VALUES[d] || d, label: d }))]} placeholder="Diet" />
          <Combobox
            testId="filter-children"
            value={filters.has_children}
            onChange={(v) => setFilters({ ...filters, has_children: v })}
            options={[{ value: "", label: "Any" }, { value: "no", label: "No children" }, { value: "yes", label: "Has children" }]}
            placeholder="Children"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button data-testid="apply-filters" onClick={applyFilters} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">
            Apply filters
          </Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {profiles.map((p) => <ProfileCard key={p.id} p={p} />)}
      </div>

      {profiles.length === 0 && !loading && (
        <div className="py-24 text-center text-[color:var(--umg-text-2)]" data-testid="empty-state">
          No profiles match your filters. Try clearing them or switching tabs.
        </div>
      )}

      <div ref={sentRef} className="h-10 mt-6 grid place-items-center text-xs text-[color:var(--umg-text-2)]">
        {loading ? "Loading…" : profiles.length >= total ? "You've reached the end." : ""}
      </div>
    </div>
  );
}
